import { effect, inject, Injectable, signal } from '@angular/core';
import { ConfigProperty, ConfigService, ImpersonationStore, PortalSettingsStore, UserStore } from '@sds/shared';
import { AnalyticsService, DataLayerService } from '@sds/shared/services';
import { hashCode, hashCodeRandom } from '@sds/shared/utils';
import { UserSettingsStore } from '@sds/user-settings';

@Injectable({
  providedIn: 'root'
})
export class UserAnalyticsInitService {
  private readonly userStore = inject(UserStore);
  private readonly portalSettingsStore = inject(PortalSettingsStore);
  private readonly userSettingsStore = inject(UserSettingsStore);
  private readonly impersonationStore = inject(ImpersonationStore);
  private readonly configService = inject(ConfigService);
  private readonly dataLayer = inject(DataLayerService);
  private readonly analyticsService = inject(AnalyticsService);

  private readonly initialized = signal(false);
  private readonly initialPageViewFired = signal(false);

  constructor() {
    effect(() => {
      if (!this.initialized()) {
        return;
      }

      this.pushUserDimensions();
      this.pushCommonDimensions();
    });
  }

  initialize(isProduction: boolean): void {
    this.pushEnvironment(isProduction);
    this.initialized.set(true);
  }

  trackNavigation(url: string): void {
    if (!this.initialized()) {
      return;
    }

    // Skip redirect-only route (ignore query params)
    const pathname = url.split('?')[0];
    if (pathname === '/') {
      return;
    }

    const isFirstPageView = !this.initialPageViewFired();
    if (isFirstPageView) {
      this.initialPageViewFired.set(true);
    }

    this.analyticsService.pageView(isFirstPageView, {}, url);
  }

  private pushEnvironment(isProduction: boolean): void {
    this.dataLayer.pushToDataLayer({ env: isProduction ? 'prod' : 'dev' });
  }

  private pushUserDimensions(): void {
    const user = this.userStore.user();
    if (user) {
      if (user.plantCount) {
        this.dataLayer.pushToDataLayer({ numberOfPlants: user.plantCount });
      }
      if (user.userGroup) {
        this.dataLayer.pushToDataLayer({ userGroup: user.userGroup });
      }
      if (user.userRoles) {
        this.dataLayer.pushToDataLayer({ userRoles: user.userRoles.map(role => role.id) });
      }

      this.dataLayer.pushToDataLayer({ language: user.languageId });
      this.dataLayer.pushToDataLayer({ ffufId: user.id });
      this.dataLayer.pushToDataLayer({ hashedId1: hashCode(user.id) });
      this.dataLayer.pushToDataLayer({ hashedId2: hashCodeRandom(user.id) });
      this.dataLayer.pushToDataLayer({ isImpersonating: this.isImpersonating() });
    } else {
      this.dataLayer.pushToDataLayer({ userGroup: 'anonymous' });
    }
  }

  private pushCommonDimensions(): void {
    const versionNumber = this.configService.get(ConfigProperty.VersionNumber);
    if (versionNumber) {
      this.dataLayer.pushToDataLayer({ versionNumber });
    }

    this.dataLayer.pushToDataLayer({ defaultCountry: this.portalSettingsStore.defaultCountry() });
    this.dataLayer.pushToDataLayer({ theme: this.userSettingsStore.theme() });
  }

  private isImpersonating(): boolean {
    const impersonation = this.impersonationStore.impersonation();
    return impersonation !== undefined && typeof impersonation.originalToken === 'string';
  }
}
